/**
 * Notification Event Type Registry
 *
 * Maps event type strings to their expected payload interfaces.
 * This is extensible: new event types can be added by module augmentation.
 *
 * Usage:
 *   // In your domain module:
 *   declare module '@zag/notifications/common' {
 *     interface NotificationEventMap {
 *       plan_published: PlanPublishedPayload;
 *       shift_assigned: ShiftAssignedPayload;
 *     }
 *   }
 */

/**
 * Extensible event type → payload map.
 * Domain modules extend this via declaration merging.
 */
export interface NotificationEventMap {
  /** Fallback for legacy/migrated notifications. */
  legacy_import: { title: string; message: string; type: string };

  /**
   * Fired once when a new user account is created —
   * either via email/password registration or the first social login.
   */
  user_account_created: { userId: string; email: string };

  /**
   * Fired once per version per tenant when the API starts with a new
   * package.json version. Sends an in-app notification to all users.
   */
  app_version_released: { version: string };

  'bulk-edit-finished': {
    jobId: string;
    collectionKey: string;
    resourceKey: string;
    totals: { succeeded: number; failed: number; skipped: number; cancelled: number };
  };

  /**
   * Employee changed their personal time override for an assigned shift unit.
   * Sent to planners responsible for the team.
   */
  shift_time_override_by_employee: {
    planInstanceId: string;
    shiftUnitId: string;
    shiftName: string;
    date: string;
    employeeId: string;
    employeeName: string;
    originalStart: string;
    originalEnd: string;
    newStart: string;
    newEnd: string;
  };

  /**
   * Planner reset an employee's personal shift override (time and/or location).
   * Sent to the affected employee.
   */
  shift_override_reset_by_planner: {
    planInstanceId: string;
    shiftUnitId: string;
    shiftName: string;
    date: string;
    plannerId: string;
    plannerName: string;
  };
}

/**
 * All registered event type strings.
 */
export type NotificationEventType = keyof NotificationEventMap;
